import { useEffect, useReducer, useRef, useState } from 'react';
import { Key } from 'ts-key-enum';
import isAppleDevice from '@sdk/utils/isAppleDevice';

import { useNavigate } from '@web/Router/router.hooks';
import { useGetConversations } from '@web/conversations/hooks/useGetConversations';

import { initialState, reducer } from './quickSearch.reducer';

export const useQuickSearch = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const listRef = useRef<HTMLOListElement | null>();
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const { data, isLoading } = useGetConversations();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;

    dispatch({ type: 'setData', payload: data });
  }, [data, isLoading]);

  useEffect(() => {
    if (!state.isOpen || !listRef.current) return;

    const listElement = listRef.current;
    const listener = () => {
      dispatch({ type: 'navigate', payload: 'wheel' });
    };

    listElement.addEventListener('wheel', listener);

    return () => {
      listElement.removeEventListener('wheel', listener);
    };
  }, [state.isOpen]);

  useEffect(() => {
    if (!activeElement) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const lastEntry = entries[entries.length - 1];
        const isVisible = lastEntry.isIntersecting;

        if (isVisible) return;

        activeElement.scrollIntoView(state.direction === 'down' ? false : true);
      },
      {
        threshold: 0.25,
      }
    );
    observer.observe(activeElement);

    return () => {
      observer.disconnect();
    };
  }, [activeElement, state.direction]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'KeyK' && (isAppleDevice() ? event.metaKey : event.ctrlKey)) {
        dispatch({ type: state.isOpen ? 'close' : 'open' });
      }

      if (!state.isOpen) {
        return;
      }

      event.stopImmediatePropagation();

      switch (event.code) {
        case Key.ArrowDown: {
          event.preventDefault();

          dispatch({
            type: 'navigate',
            payload: 'down',
          });
          break;
        }
        case Key.ArrowUp: {
          event.preventDefault();

          dispatch({
            type: 'navigate',
            payload: 'up',
          });
          break;
        }
        case Key.Enter: {
          navigate.toConversationDetails({
            params: {
              id: state.activeConversation.id,
            },
          });
          dispatch({ type: 'close' });
          break;
        }
        case Key.Escape: {
          dispatch({ type: 'close' });
          break;
        }
      }
    };

    document.addEventListener('keydown', listener, { capture: true });

    return () => {
      document.removeEventListener('keydown', listener, { capture: true });
    };
  }, [state.isOpen, state.activeConversation, navigate]);

  const onQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'queryChange',
      payload: e.target.value,
    });
  };

  const onItemClick = () => {
    dispatch({ type: 'close' });
  };

  return { listRef, onItemClick, onQueryChange, setActiveElement, state };
};
